import type { RootState, RootStore } from "~redux/store";
import {
    updateGIMessage,
    edstSlice,
    setDPosConfig,
    setEdstState,
    setGIMessages,
    setEdsmConfig,
    setWindGridGeomapConfig,
    setMapCenter,
    openView,
} from "~redux/slices/edstSlice";
import type { EramHubConnection } from "@poscon/shared-frontend";
import {
    viewMenuFieldsSlice,
    viewOptionSliceName,
    setAltimeterList,
    setMetarList,
    baseEramHubConnection,
    isRestrictedUiAction,
    messagingSlice,
    uiSubscribedSelector,
    geomapStore,
    setMraMessage,
    setUiSubscribed,
} from "@poscon/shared-frontend";
import {
    deleteEdstFps,
    setAclAckList,
    setAclSpaList,
    setAclUnackList,
    setDepAckList,
    setDepSpaList,
    setDepUnackList,
    setEdstFlightplans,
    setFpTrackPositions,
    setMtcaConflicts,
    updateEdstFlightplan,
    updateEdstFlightplans,
    updateTrajectories,
} from "~redux/slices/edstFlightplanSlice";
import { aclSlice, setAclPostingMode, setAclSort, setAclState } from "~redux/slices/aclSlice";
import { depSlice, setDepPostingMode, setDepSort, setDepState } from "~redux/slices/depSlice";
import { env } from "~/env";
import { setTrialPlanQueue } from "~redux/slices/trailPlanSlice";
import type { DefaultEventsMap } from "@socket.io/component-emitter";
import { setViewOptionState } from "~redux/slices/viewOptionSlice";
import { listenerMiddleware } from "~redux/listenerMiddleware";
import { gpdSlice, setGpdGeomapConfig, setGpdState } from "~redux/slices/gpdSlice";
import { WebviewWindow } from "@tauri-apps/api/webviewWindow";
import { isAction } from "@reduxjs/toolkit";
import { EdstClientEventMap, ArtccId } from "@poscon/shared-types";
import { ControllerSectorId, PosconRole } from "@poscon/shared-types/poscon";
import { Geomap, GeomapSaaFeature } from "@poscon/shared-types/eram";

const syncedUiSlices = [
    edstSlice.name,
    viewOptionSliceName,
    aclSlice.name,
    depSlice.name,
    gpdSlice.name,
] as const;
type SyncedUiSlice = (typeof syncedUiSlices)[number];

let store: RootStore;
export const ERAM_SERVER_URL = env.VITE_ERAM_SERVER_URL;
export const eramHubConnection = baseEramHubConnection as EramHubConnection<
    EdstClientEventMap & DefaultEventsMap
>;
eramHubConnection.url = ERAM_SERVER_URL;

export const injectStore = async (s: RootStore) => {
    store = s;
    eramHubConnection.dispatch = store.dispatch;
    eramHubConnection.getState = () => {
        const state = store.getState();
        return Object.fromEntries(syncedUiSlices.map((slice) => [slice, state[slice]]));
    };
    eramHubConnection.registerEventHandler("setEdstResponseAreaMessage", ([message]) => {
        store.dispatch(setMraMessage(message));
    });

    eramHubConnection.registerEventHandler("subscribeToEdstUiState", ([v]) => {
        store.dispatch(setUiSubscribed(v));
    });

    eramHubConnection.registerEventHandler("receiveEdstUiState", ([_state]) => {
        const state = _state as Pick<RootState, SyncedUiSlice>;
        store.dispatch(setViewOptionState(state.viewOptions));
        store.dispatch(setEdstState(state.edst));
        store.dispatch(setAclState(state.acl));
        store.dispatch(setDepState(state.dep));
        store.dispatch(setGpdState(state.gpd));
    });
    eramHubConnection.registerEventHandler("receiveEdstUiAction", ([action]) => {
        if (isAction(action)) {
            store.dispatch({ ...action, meta: { forwarded: true } });
        }
    });
    eramHubConnection.registerEventHandler("raiseView", ([view]) => {
        store.dispatch(openView(view))
    });
    eramHubConnection.registerEventHandler("setRangeCenter", ([rangeCenter]) => {
        store.dispatch(setMapCenter([rangeCenter.centerLongitude, rangeCenter.centerLatitude]));
    });
    eramHubConnection.registerEventHandler("receiveEdstAltimeterList", ([altimeterList]) => {
        store.dispatch(setAltimeterList(altimeterList));
    });
    eramHubConnection.registerEventHandler("receiveEdstMetarList", ([metarList]) => {
        store.dispatch(setMetarList(metarList));
    });
    eramHubConnection.registerEventHandler("receiveAclPostingMode", ([postingMode]) => {
        store.dispatch(setAclPostingMode(postingMode));
    });
    eramHubConnection.registerEventHandler("receiveDepPostingMode", ([postingMode]) => {
        store.dispatch(setDepPostingMode(postingMode));
    });
    eramHubConnection.registerEventHandler("receiveAclSortMethod", ([{ method, sortBySector }]) => {
        store.dispatch(setAclSort({ selectedOption: method, sector: sortBySector }));
    });
    eramHubConnection.registerEventHandler("receiveDepSortMethod", ([sortMethod]) => {
        store.dispatch(setDepSort(sortMethod));
    });
    eramHubConnection.registerEventHandler("receiveAclLists", ([{ unackList, ackList, spaList }]) => {
        store.dispatch(setAclUnackList(unackList));
        store.dispatch(setAclAckList(ackList));
        store.dispatch(setAclSpaList(spaList));
    });
    eramHubConnection.registerEventHandler("receiveDepLists", ([{ unackList, ackList, spaList }]) => {
        store.dispatch(setDepUnackList(unackList));
        store.dispatch(setDepAckList(ackList));
        store.dispatch(setDepSpaList(spaList));
    });
    eramHubConnection.registerEventHandler("receiveTrialPlans", ([trialPlans]) => {
        store.dispatch(setTrialPlanQueue(trialPlans));
    });
    eramHubConnection.registerEventHandler("receiveEdstFpMap", ([fpMap]) => {
        store.dispatch(setEdstFlightplans(fpMap));
    });
    eramHubConnection.registerEventHandler("updateEdstFp", ([{ fpId, data }]) => {
        store.dispatch(updateEdstFlightplan({ fpId, data }));
    });
    eramHubConnection.registerEventHandler("updateEdstFlightplans", ([data]) => {
        store.dispatch(updateEdstFlightplans(data));
    });
    eramHubConnection.registerEventHandler("deleteEdstFps", ([ids]) => {
        store.dispatch(deleteEdstFps(ids));
    });
    eramHubConnection.registerEventHandler("receiveFpTrackPositions", ([fpTrackPositions]) => {
        store.dispatch(setFpTrackPositions(fpTrackPositions));
    });
    eramHubConnection.registerEventHandler("receiveTrajectories", ([trajectories]) => {
        store.dispatch(updateTrajectories(trajectories));
    });
    eramHubConnection.registerEventHandler("receiveMtcaConflicts", ([pairs]) => {
        store.dispatch(setMtcaConflicts(pairs));
    });
    eramHubConnection.registerEventHandler("receiveGiMessages", ([messages]) => {
        store.dispatch(setGIMessages(messages));
    });
    eramHubConnection.registerEventHandler("receiveGiMessage", ([msg]) => {
        store.dispatch(updateGIMessage(msg));
    });
};

// TODO: put into env file
const cdnUrl = "https://cdn.poscon.com";

export async function initializeConnection(
    artccId: ArtccId,
    sectorId: ControllerSectorId | null = null,
    role: PosconRole = "Data",
) {
    await eramHubConnection.initialize(artccId, "EDST", sectorId, role);
    try {
        geomapStore.geomapConfigs = await eramHubConnection.getGeomapConfigs(artccId);
        const geomapIds = [...new Set(geomapStore.geomapConfigs.flatMap((c) => c.geomapIds))];
        const saaGeomapIds = [...new Set(geomapStore.geomapConfigs.flatMap((c) => c.saaGeomapIds))];
        const geomaps = (
            await Promise.allSettled(
                geomapIds.map(async (id) => {
                    const res = await fetch(`${cdnUrl}/videomaps/eram/${artccId}/${id}.geojson`);
                    const v = await res.json();
                    return v;
                }),
            )
        )
            .filter((v): v is PromiseFulfilledResult<Geomap> => v.status === "fulfilled")
            .map(({ value }) => value);
        const saaMaps = (
            await Promise.allSettled(
                saaGeomapIds.map(async (id) => {
                    const res = await fetch(`${cdnUrl}/videomaps/eram/${artccId}/${id}.geojson`);
                    const v = await res.json();
                    return v;
                }),
            )
        )
            .filter((v): v is PromiseFulfilledResult<Geomap<GeomapSaaFeature>> => v.status === "fulfilled")
            .map(({ value }) => value);
        geomapStore.saaGeomaps = saaMaps;
        geomapStore.geomaps = geomaps;
        geomapStore.mapFilterMenus = await eramHubConnection.getFilterMenu(artccId);
    } catch (e) {
        console.error("failed to load geomaps: ", e);
    }
    eramHubConnection.getDposConfig(artccId).then((dPosConfig) => {
        store.dispatch(setDPosConfig(dPosConfig));
    });
    eramHubConnection.getEdstGeomapConfig(artccId).then((config) => {
        store.dispatch(setWindGridGeomapConfig(config.windGrid));
        store.dispatch(setGpdGeomapConfig(config.gpd));
    });
    eramHubConnection.getEDSMConfig(artccId).then((config) => {
        store.dispatch(
            setEdsmConfig({ areas: config.areas, defaultCombinedSectors: config.defaultCombinedSectors }),
        );
    });
}

export const uiActionReducerNames: string[] = [
    viewOptionSliceName,
    edstSlice.name,
    messagingSlice.name,
    aclSlice.name,
    depSlice.name,
    gpdSlice.name,
    viewMenuFieldsSlice.name,
];

listenerMiddleware.startListening({
    predicate: (action, currentState) =>
        uiSubscribedSelector(currentState as any) && isRestrictedUiAction(action, uiActionReducerNames),
    effect: (action) => {
        eramHubConnection.emit("edstUiAction", action);
    },
});

if (window.__TAURI__) {
    const webviewWindow = WebviewWindow.getCurrent();
    await webviewWindow.listen("poscon:close", async () => {
        if (eramHubConnection.isActive && eramHubConnection.role === "Data") {
            try {
                await eramHubConnection.signout("EDST");
            } catch (err) {
                console.error(err);
            }
        }
        void webviewWindow.close();
    });
}
